import { useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  DollarSign, Plus, X, Edit3, Trash2, RefreshCw,
  ArrowLeftRight, Zap, Settings2, ToggleRight,
  Search, ChevronLeft, ChevronRight, AlertCircle, CheckCircle2
} from "lucide-react";

// ─── Data ────────────────────────────────────────────────────────────────────

const INITIAL_CURRENCIES = [
  { id: 1, code: "INR", name: "Indian Rupee",      symbol: "₹",   flag: "🇮🇳", rateToINR: 1,      status: "active",   updated: "2 min ago",  isBase: true  },
  { id: 2, code: "USD", name: "US Dollar",         symbol: "$",   flag: "🇺🇸", rateToINR: 83.12,  status: "active",   updated: "2 min ago",  isBase: false },
  { id: 3, code: "EUR", name: "Euro",              symbol: "€",   flag: "🇪🇺", rateToINR: 90.69,  status: "active",   updated: "5 min ago",  isBase: false },
  { id: 4, code: "GBP", name: "British Pound",     symbol: "£",   flag: "🇬🇧", rateToINR: 106.03, status: "active",   updated: "5 min ago",  isBase: false },
  { id: 5, code: "AED", name: "UAE Dirham",        symbol: "د.إ", flag: "🇦🇪", rateToINR: 22.64,  status: "inactive", updated: "1 hr ago",   isBase: false },
  { id: 6, code: "SGD", name: "Singapore Dollar",  symbol: "S$",  flag: "🇸🇬", rateToINR: 61.77,  status: "active",   updated: "10 min ago", isBase: false },
  { id: 7, code: "AUD", name: "Australian Dollar", symbol: "A$",  flag: "🇦🇺", rateToINR: 54.58,  status: "inactive", updated: "30 min ago", isBase: false },
];

const DEFAULT_TOGGLES = {
  autoConversion:  true,
  showSymbols:     true,
  liveRates:       true,
  clientSwitching: false,
  roundPrices:     true,
};

const TOGGLE_META = [
  { key: "autoConversion",  label: "Auto Conversion",      desc: "Automatically convert prices for all users" },
  { key: "showSymbols",     label: "Show Currency Symbols", desc: "Display ₹, $, £ symbols next to prices"    },
  { key: "liveRates",       label: "Live Exchange Rates",  desc: "Fetch rates via API every 15 minutes"       },
  { key: "clientSwitching", label: "Client-side Switch",   desc: "Allow users to change display currency"     },
  { key: "roundPrices",     label: "Round Prices",         desc: "Round to nearest whole number after convert"},
];

const BLANK_FORM = {
  code: "", name: "", symbol: "", flag: "", rateToINR: "", status: "active",
};

// ─── Primitives ──────────────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div className="bg-gray-100 rounded-2xl p-4 sm:p-5 space-y-3">
    <p className="text-sm font-semibold text-gray-700">{title}</p>
    {children}
  </div>
);

const FieldInput = ({ label, value, onChange, type = "text", error, placeholder }) => (
  <div>
    <label className="text-xs sm:text-sm flex items-center gap-2 mb-1">
      <span className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0" />
      {label}
    </label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-3 sm:px-4 py-2.5 rounded-xl border outline-none bg-white text-sm ${
        error ? "border-red-400 bg-red-50" : "border-gray-200"
      }`}
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const FieldSelect = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-xs sm:text-sm flex items-center gap-2 mb-1">
      <span className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0" />
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 sm:px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const StatusBtn = ({ value, current, set }) => {
  const active = current === value;
  const style = active
    ? value === "active"
      ? { backgroundColor: "#dcfce7", color: "#15803d" }
      : { backgroundColor: "#fee2e2", color: "#b91c1c" }
    : { backgroundColor: "#f3f4f6", color: "#6b7280" };
  return (
    <button type="button" onClick={() => set(value)} style={style}
      className="px-4 py-2 rounded-full text-xs sm:text-sm capitalize font-medium">
      {value}
    </button>
  );
};

const Toggle = ({ checked, onChange }) => (
  <button type="button" onClick={onChange}
    style={{ backgroundColor: checked ? "#0d9488" : "#d1d5db" }}
    className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200">
    <span
      style={{ transform: checked ? "translateX(1.375rem)" : "translateX(0.25rem)" }}
      className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200"
    />
  </button>
);

const Stat = ({ label, value, color = "text-gray-900" }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-3 sm:p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-xl sm:text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const ActionBtns = ({ onEdit, onDelete }) => (
  <div className="flex items-center gap-2">
    <button type="button" onClick={onEdit} className="text-teal-600 hover:text-teal-800 p-1">
      <Edit3 size={15} />
    </button>
    <button type="button" onClick={onDelete} className="text-red-500 hover:text-red-700 p-1">
      <Trash2 size={15} />
    </button>
  </div>
);

// ─── Conversion Simulator (inline widget) ────────────────────────────────────

const ConversionSim = ({ currencies }) => {
  const [amount, setAmount] = useState("1000");
  const [from, setFrom]   = useState("INR");
  const [to, setTo]       = useState("USD");

  const fc = currencies.find((c) => c.code === from) || currencies[0];
  const tc = currencies.find((c) => c.code === to)   || currencies[1];
  const n  = parseFloat(amount) || 0;
  const inr = n * fc.rateToINR;
  const result = inr / tc.rateToINR;

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <ArrowLeftRight size={16} className="text-teal-600" />
        <span className="text-sm font-semibold text-gray-800">Conversion Simulator</span>
        <span className="ml-auto flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
          <Zap size={10} /> Live
        </span>
      </div>
      <div className="flex flex-col sm:flex-row items-end gap-2 sm:gap-3">
        <div className="w-full sm:flex-1">
          <p className="text-[11px] text-gray-400 mb-1 uppercase tracking-widest font-semibold">Amount & From</p>
          <div className="flex gap-2">
            <input
              type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-24 border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <select value={from} onChange={(e) => setFrom(e.target.value)}
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500">
              {currencies.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
            </select>
          </div>
        </div>
        <button onClick={swap}
          className="w-9 h-9 rounded-full bg-teal-600 hover:bg-teal-700 text-white flex items-center justify-center transition-all hover:rotate-180 shrink-0 mb-0.5">
          <ArrowLeftRight size={14} />
        </button>
        <div className="w-full sm:flex-1">
          <p className="text-[11px] text-gray-400 mb-1 uppercase tracking-widest font-semibold">To</p>
          <select value={to} onChange={(e) => setTo(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500">
            {currencies.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-3 bg-teal-50 border border-teal-100 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <p className="text-xs text-teal-600">{fc.symbol}{parseFloat(amount||0).toLocaleString()} =</p>
          <p className="text-xl font-bold text-teal-800">{tc.symbol}{result.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">Updated 2m ago</p>
        </div>
        <div className="bg-white rounded-xl px-4 py-2.5 border border-teal-100 text-right">
          <p className="text-[11px] text-gray-400">Reverse rate</p>
          <p className="text-sm font-semibold text-gray-800">1 {to} = {fc.symbol}{(tc.rateToINR/fc.rateToINR).toFixed(4)}</p>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function CurrencySettings() {
  const [currencies, setCurrencies] = useState(INITIAL_CURRENCIES);
  const [toggles, setToggles]       = useState(DEFAULT_TOGGLES);
  const [search, setSearch]         = useState("");
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [saved, setSaved]           = useState(false);
  const [page, setPage]             = useState(1);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(BLANK_FORM);
  const [errors, setErrors]         = useState({});
  const [activeTab, setActiveTab]   = useState("General");
  const PER_PAGE = 5;

  const setF = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));
  const markUnsaved = () => { setHasUnsaved(true); setSaved(false); };

  const filtered = useMemo(() =>
    currencies.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.code.toLowerCase().includes(search.toLowerCase())
    ), [currencies, search]);

  const pages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const maxRate = Math.max(...currencies.map((c) => c.rateToINR));

  const toggleStatus = (id) => {
    setCurrencies((prev) => prev.map((c) => c.id === id
      ? { ...c, status: c.status === "active" ? "inactive" : "active" }
      : c));
    markUnsaved();
  };

  const handleToggleFeature = (key) => {
    setToggles((p) => ({ ...p, [key]: !p[key] }));
    markUnsaved();
  };

  const openAdd = () => {
    setEditing(null);
    setForm(BLANK_FORM);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      code: item.code, name: item.name, symbol: item.symbol,
      flag: item.flag, rateToINR: String(item.rateToINR), status: item.status,
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this currency?")) {
      setCurrencies((prev) => prev.filter((c) => c.id !== id));
      markUnsaved();
    }
  };

  const save = () => {
    const errs = {};
    if (!form.code.trim())       errs.code   = "Currency code is required.";
    if (!form.name.trim())       errs.name   = "Currency name is required.";
    if (!form.symbol.trim())     errs.symbol = "Symbol is required.";
    if (!form.rateToINR || isNaN(parseFloat(form.rateToINR)))
      errs.rateToINR = "Enter a valid numeric rate.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});

    if (editing) {
      setCurrencies((prev) => prev.map((c) => c.id === editing.id
        ? { ...c, ...form, rateToINR: parseFloat(form.rateToINR), updated: "just now" }
        : c));
    } else {
      setCurrencies((prev) => [...prev, {
        id: Date.now(), ...form,
        rateToINR: parseFloat(form.rateToINR),
        isBase: false, updated: "just now",
      }]);
    }
    setModalOpen(false);
    markUnsaved();
  };

  const handleSave = () => {
    setHasUnsaved(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    setCurrencies(INITIAL_CURRENCIES);
    setToggles(DEFAULT_TOGGLES);
    markUnsaved();
  };

  const TABS = ["General", "Exchange Rates", "Display Rules", "Audit Log"];

  return (
    <div className="min-h-screen bg-gray-50 font-sans">

      {/* ── Header ── */}
      <div className=" ">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-5 pb-0">
          {/* breadcrumb */}
          

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-700 rounded-xl flex items-center justify-center shadow-sm shrink-0">
                <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-teal-800">Currency Settings</h1>
                <p className="text-sm text-gray-600">Manage global currency conversion</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              
              <button onClick={openAdd}
                className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2.5 rounded-xl hover:bg-teal-700 transition text-sm">
                <Plus size={16} /> Add Currency
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 space-y-4 sm:space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <Stat label="Total Currencies" value={currencies.length} />
          <Stat label="Active"           value={currencies.filter((c) => c.status === "active").length}   color="text-green-600" />
          <Stat label="Inactive"         value={currencies.filter((c) => c.status === "inactive").length} color="text-red-600" />
          <Stat label="Last Sync"        value="2m ago" color="text-teal-600" />
        </div>

        {/* Feature Toggles */}
       

        {/* Conversion Simulator */}
        <ConversionSim currencies={currencies} />

        {/* Search */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 sm:p-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              placeholder="Search currencies..."
              className="pl-9 pr-4 py-2 border rounded-xl w-full text-sm border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden [@media(min-width:700px)]:block overflow-x-auto bg-white rounded-2xl shadow-sm border border-gray-100">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-teal-700 text-white">
              <tr>
                {["Currency", "Symbol", "Rate (INR)", "Status", "Updated", "Actions"].map((h) => (
                  <th key={h} className="px-4 lg:px-5 py-3 font-medium whitespace-nowrap text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">No currencies found</td></tr>
              ) : paginated.map((c) => (
                <tr key={c.id} className="border-t hover:bg-gray-50 transition-colors">
                  <td className="px-4 lg:px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xl">{c.flag}</span>
                      <div>
                        <p className="font-medium text-gray-800 text-[13px]">{c.name}</p>
                        <p className="text-[11px] text-gray-400">{c.code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 lg:px-5 py-3.5">
                    <span className="w-8 h-8 rounded-lg bg-teal-50 text-teal-700 text-sm font-medium flex items-center justify-center">
                      {c.symbol}
                    </span>
                  </td>
                  <td className="px-4 lg:px-5 py-3.5 font-medium text-gray-800">
                    {c.isBase ? <span className="text-teal-600 font-semibold">Base</span> : c.rateToINR.toFixed(2)}
                  </td>
                  
                  <td className="px-4 lg:px-5 py-3.5">
                    <Toggle checked={c.status === "active"} onChange={() => toggleStatus(c.id)} />
                  </td>
                  <td className="px-4 lg:px-5 py-3.5 text-xs text-gray-400">{c.updated}</td>
                  <td className="px-4 lg:px-5 py-3.5">
                    <ActionBtns onEdit={() => openEdit(c)} onDelete={() => handleDelete(c.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="[@media(min-width:700px)]:hidden space-y-3">
          {paginated.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No currencies found</div>
          ) : paginated.map((c) => (
            <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 space-y-2">
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xl">{c.flag}</span>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{c.name}</p>
                    <p className="text-xs text-gray-500">{c.code} · {c.symbol}</p>
                  </div>
                </div>
                <ActionBtns onEdit={() => openEdit(c)} onDelete={() => handleDelete(c.id)} />
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-500">
                <p>Rate: {c.isBase ? "Base" : `₹${c.rateToINR.toFixed(2)}`}</p>
                <p className="text-right">Updated: {c.updated}</p>
              </div>
              <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {c.status}
                </span>
                <Toggle checked={c.status === "active"} onChange={() => toggleStatus(c.id)} />
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-xs text-gray-400">
              Showing {Math.min((page-1)*PER_PAGE+1,filtered.length)}–{Math.min(page*PER_PAGE,filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setPage((p) => Math.max(1, p-1))} disabled={page===1}
                className="w-8 h-8 rounded-lg border border-gray-200 bg-white hover:border-teal-400 text-gray-500 flex items-center justify-center disabled:opacity-40">
                <ChevronLeft size={13} />
              </button>
              {Array.from({ length: pages }, (_, i) => i+1).map((p) => (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg border text-xs font-medium transition-all ${p===page ? "bg-teal-600 text-white border-teal-600" : "border-gray-200 bg-white text-gray-600 hover:border-teal-400"}`}>
                  {p}
                </button>
              ))}
              <button onClick={() => setPage((p) => Math.min(pages, p+1))} disabled={page===pages}
                className="w-8 h-8 rounded-lg border border-gray-200 bg-white hover:border-teal-400 text-gray-500 flex items-center justify-center disabled:opacity-40">
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}

      </div>

   

      {/* ── Side-drawer Modal ── */}
      {modalOpen && createPortal(
        <div className="fixed inset-0 z-50 flex justify-center sm:justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={() => setModalOpen(false)} />
          <div
            className="relative w-full sm:max-w-md h-full bg-white shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Teal header */}
            <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white tracking-tight">
                {editing ? "Edit Currency" : "Add New Currency"}
              </h2>
              <button type="button" onClick={() => setModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                <X size={16} className="text-white" />
              </button>
            </div>

            {/* Modal Form */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
              <Section title="Currency Info">
                <FieldInput label="Currency Code *" value={form.code} onChange={setF("code")}
                  error={errors.code} placeholder="e.g. USD" />
                <FieldInput label="Currency Name *" value={form.name} onChange={setF("name")}
                  error={errors.name} placeholder="e.g. US Dollar" />
                <FieldInput label="Symbol *" value={form.symbol} onChange={setF("symbol")}
                  error={errors.symbol} placeholder="e.g. $" />
                <FieldInput label="Flag Emoji" value={form.flag} onChange={setF("flag")}
                  placeholder="e.g. 🇺🇸" />
              </Section>

              <Section title="Exchange Rate">
                <FieldInput label="Rate to INR *" type="number" value={form.rateToINR}
                  onChange={setF("rateToINR")} error={errors.rateToINR}
                  placeholder="e.g. 83.12" />
                <p className="text-[11px] text-gray-500">How many INR equals 1 unit of this currency</p>
              </Section>

              <Section title="Status">
                <div className="flex gap-2 sm:gap-3">
                  <StatusBtn value="active"   current={form.status} set={setF("status")} />
                  <StatusBtn value="inactive" current={form.status} set={setF("status")} />
                </div>
              </Section>

              <div className="flex gap-3 sm:gap-4 pt-2 pb-4">
                <button type="button" onClick={() => setModalOpen(false)}
                  className="flex-1 py-2.5 sm:py-3 rounded-xl border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition text-sm">
                  Cancel
                </button>
                <button type="button" onClick={save}
                  className="flex-1 py-2.5 sm:py-3 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 transition text-sm">
                  {editing ? "Update" : "Add Currency"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}